import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { Gift, Loader2 } from "lucide-react";
import { saveGift } from "@/lib/cart";
import { deviceId } from "@/lib/game";

const API = process.env.REACT_APP_BACKEND_URL;

export default function GiftPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [won, setWon] = useState(null);

  useEffect(() => {
    if (!API) { setErr("Gift links aren't available right now."); return; }
    axios.get(`${API}/api/gift/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(e?.response?.data?.detail || "This gift link isn't valid."));
  }, [token]);

  const claim = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await axios.post(`${API}/api/gift/${token}/claim`, { email, device_id: deviceId() });
      saveGift({ code: r.data.code });
      setWon(r.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't claim this gift.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="gift-page" className="pt-36 pb-24 min-h-[80vh]">
      <div className="max-w-lg mx-auto px-4 text-center">
        <div className="mx-auto h-16 w-16 rounded-full bg-[#FFB21A] grid place-items-center mb-6"><Gift className="h-8 w-8" /></div>
        {won ? (
          <>
            <h1 className="font-display text-4xl sm:text-5xl leading-none">{won.pct}% off is yours!</h1>
            <p className="mt-4 text-[#4A4560]">It's saved and applied automatically at checkout.</p>
            <p data-testid="gift-code" className="mt-6 rounded-2xl border-2 border-dashed border-[#1B1530] bg-white py-3 font-display text-3xl tracking-wider">{won.code}</p>
            <Link to="/build?size=12" data-testid="gift-shop-button" className="mt-6 inline-flex h-12 px-8 items-center rounded-full bg-[#FF5A5F] text-white font-bold hover:bg-[#1B1530] transition-colors">Build my box</Link>
          </>
        ) : (
          <>
            <h1 className="font-display text-4xl sm:text-5xl leading-none">{info ? `A friend sent you ${info.pct}% off Swole Bears` : "A gift for you"}</h1>
            <p className="mt-4 text-[#4A4560]">22g protein gummy bears with 75% less sugar. Enter your email to unlock your first-order discount.</p>
            {info?.claimed ? (
              <p data-testid="gift-error" className="mt-6 font-bold text-[#E8233F]">This gift has already been claimed.</p>
            ) : (
              <form onSubmit={claim} className="mt-6 flex flex-col sm:flex-row gap-3">
                <input data-testid="gift-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
                  className="flex-1 h-12 rounded-full bg-white border border-[#1B1530]/15 px-5 outline-none focus:ring-2 focus:ring-[#1B1530]" />
                <button data-testid="gift-claim-button" disabled={busy || !info} className="h-12 px-6 rounded-full bg-[#1B1530] text-white font-bold hover:bg-[#FF5A5F] transition-colors disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Claim my gift"}
                </button>
              </form>
            )}
            {err && <p data-testid="gift-error" className="mt-4 text-sm font-bold text-[#E8233F]">{err}</p>}
          </>
        )}
      </div>
    </section>
  );
}
